/**
 * ConfirmationPage Component
 * QP Submission page with complete BRR display
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, FileText, Package, Globe, Clock, ExternalLink, Loader2, Plus, Layers, X } from 'lucide-react';
import { BRRApiClient, SessionAuditClient, SessionStatus } from '../services';
import { AdditionalEvidenceItem, FieldOverrides, ValidationNote, Document, QPReviewData, ManualChangeControl, ManualQualityEvent, BRRFormData, STANDARD_DOC_SECTIONS, CustomDocumentSection, CP_INTERNAL_DOC_ITEMS, PackagingMaterialEntry, MaterialUsedEntry, getComplianceOptionsForField, getComplianceDisplayLabel, FIELD_COMPLIANCE_TYPE, BoxSubsectionData, BoxLinkEntry, TraceLinkHierarchyLevel, ProgressStep } from '../services/types';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { formatNumber, formatBatchId } from '../utils/formatNumber';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { DeleteBRRButton } from '../components';
import { NOT_FOUND_VALUE } from '../components/ManufacturerDropdown';

type BRRLevel = 'level1' | 'level2' | 'na';

// Progress tracking step definitions - MUST be outside component to avoid re-creation on every render
const CONFIRMATION_PAGE_LOAD_STEPS: ProgressStep[] = [
  { name: 'prepare', label: 'Preparing...' },
  { name: 'getSavedReview', label: 'Loading saved review...', weight: 2 },
  { name: 'mergeOverrides', label: 'Applying field overrides...' },
  { name: 'processData', label: 'Processing form data...' },
  { name: 'complete', label: 'Finalizing...' },
];

const CONFIRMATION_PAGE_SAVE_STEPS: ProgressStep[] = [
  { name: 'prepare', label: 'Preparing to save...' },
  { name: 'buildPayload', label: 'Building save payload...' },
  { name: 'saveToDb', label: 'Saving to database...', weight: 2 },
  { name: 'updateMetadata', label: 'Updating session metadata...' },
  { name: 'complete', label: 'Save complete' },
];

interface ConfirmationPageProps {
  batchId: string;
  inspectionLotId?: string;
  selectedLotIds?: string[];
  indirectTransportLotId?: string | null;
  sessionId: string | null;
  userEmail: string;
  apiClient: BRRApiClient;
  sessionClient: SessionAuditClient;
  onSubmit: (qpName: string, qpEmail: string) => void;
  onCancel: () => void;
  fieldOverrides: FieldOverrides;
  setFieldOverrides: React.Dispatch<React.SetStateAction<FieldOverrides>>;
  complianceOverrides: Record<string, string>;
  setComplianceOverrides: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  data: any; // autoPopulatedBRR structure
  additionalEvidenceItems?: AdditionalEvidenceItem[];
  setAdditionalEvidenceItems?: React.Dispatch<React.SetStateAction<AdditionalEvidenceItem[]>>;
  onDeleteBRR?: () => Promise<void>;
  blockNavigation?: (message: string) => void;
  unblockNavigation?: (error?: string | null) => void;
  isNavigationBlocked?: boolean;
  startProgressOperation?: (operationId: string, steps: ProgressStep[], title?: string) => void;
  advanceProgressStep?: (stepLabel?: string) => void;
  brrLevel?: BRRLevel | null;
  isActive?: boolean;
}

// Helper to format BRR level for display
const formatBrrLevel = (level: BRRLevel | null | undefined): string => {
  if (level === 'level1') return 'Level 1';
  if (level === 'level2') return 'Level 2';
  if (level === 'na') return 'N/A';
  return 'Not Set';
};

// Only evidence that predates the move to Box has a directly linkable URL; Box-hosted files
// are reached through the batch's Box archive instead.
const hasDirectBlobLink = (item: AdditionalEvidenceItem): boolean => !!item.url && !item.boxFileId;

// Manufacturer fields that support "Value not in list" with manual entry
const MANUFACTURER_FIELDS = new Set([
  'apiManufacturer', 'bulkManufacturer', 'qcTestingSite',
  'primaryPackagingSite', 'secondaryPackagingSite', 'releaseSite'
]);

const ConfirmationPageComponent: React.FC<ConfirmationPageProps> = ({
  batchId,
  inspectionLotId: inspectionLotIdProp,
  selectedLotIds,
  indirectTransportLotId,
  sessionId,
  userEmail,
  apiClient,
  sessionClient,
  onSubmit,
  onCancel,
  fieldOverrides,
  setFieldOverrides,
  complianceOverrides,
  setComplianceOverrides,
  data,
  additionalEvidenceItems,
  setAdditionalEvidenceItems,
  onDeleteBRR,
  blockNavigation,
  unblockNavigation,
  isNavigationBlocked,
  startProgressOperation,
  advanceProgressStep,
  brrLevel,
  isActive
}) => {
  // Feature flag: Controlled via centralized config (src/config/featureFlags.ts)
  const { ENABLE_SAVE_FUNCTIONALITY } = FEATURE_FLAGS;

  // State for save/load functionality
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  // State for save-before-download operations
  const [savingBeforeDownload, setSavingBeforeDownload] = useState<'word' | null>(null);

  // Saved auto-populated data from Neo4j (for cloned BRRs when data prop is empty)
  const [savedAutoPopulatedData, setSavedAutoPopulatedData] = useState<any>(null);

  // Track if this is a cloned session (affects data loading behavior)
  const [isClonedSession, setIsClonedSession] = useState(false);

  // State for brrForm data (loaded from Neo4j to apply CC/QE modifications)
  const [removedCCIds, setRemovedCCIds] = useState<Set<string>>(new Set());
  const [manuallyAddedCCs, setManuallyAddedCCs] = useState<ManualChangeControl[]>([]);
  const [removedQEIds, setRemovedQEIds] = useState<Set<string>>(new Set());
  const [manuallyAddedQEs, setManuallyAddedQEs] = useState<ManualQualityEvent[]>([]);
  const [changeControlNotes, setChangeControlNotes] = useState<Array<{ text: string; timestamp: string }>>([]);
  const [individualCCNotes, setIndividualCCNotes] = useState<Record<string, Array<{ text: string; timestamp: string }>>>({});
  const [countrySpecificRequirements, setCountrySpecificRequirements] = useState<string>('');
  const [brrCreationBasis, setBrrCreationBasis] = useState<string[]>([]);
  const [cpInternalDocs, setCpInternalDocs] = useState<string[]>([]);
  const [isEditingCpDocs, setIsEditingCpDocs] = useState(false);
  const [editingCpDocs, setEditingCpDocs] = useState<string[]>([]);
  const [cpDocsEditedByQP, setCpDocsEditedByQP] = useState(false);
  const [nextPeriodicReview, setNextPeriodicReview] = useState<string>('');

  // Serialization & Aggregation data from document_validation
  const [serializationRequirement, setSerializationRequirement] = useState<string>('');
  const [aggregationRequirement, setAggregationRequirement] = useState<string>('');
  const [documentSectionData, setDocumentSectionData] = useState<Record<string, { version: string; freeText: string }>>({});
  const [customDocSections, setCustomDocSections] = useState<CustomDocumentSection[]>([]);
  const [boxFolderUrls, setBoxFolderUrls] = useState<Record<string, BoxSubsectionData>>({});
  const [tracelinkGtin, setTracelinkGtin] = useState<string>('');
  const [tracelinkQuantity, setTracelinkQuantity] = useState<string>('');
  const [tracelinkHierarchyLevels, setTracelinkHierarchyLevels] = useState<TraceLinkHierarchyLevel[]>([]);

  // Packaging Materials (KAU) state — read-only display (managed on FormPage)
  const [packagingMaterials, setPackagingMaterials] = useState<PackagingMaterialEntry[]>([]);

  // Materials Used state — read-only display (managed on FormPage)
  const [materialsUsed, setMaterialsUsed] = useState<MaterialUsedEntry[]>([]);

  // GMP Annex 16 Batch Release Site confirmation
  const [batchReleaseSiteText, setBatchReleaseSiteText] = useState<string>('');
  const [batchReleaseSiteConfirmed, setBatchReleaseSiteConfirmed] = useState<boolean>(false);
  const [batchReleaseSiteCheplapharmConfirmed, setBatchReleaseSiteCheplapharmConfirmed] = useState<boolean>(false);
  const [selectedReleaseSites, setSelectedReleaseSites] = useState<string[]>([]);

  // State for editing additional evidence items
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [editingBoxLink, setEditingBoxLink] = useState('');

  // Track previous sessionId to detect changes
  const previousSessionIdRef = useRef<string | null>(null);

  // Convert batchId to number for API
  const batchNumber = parseInt(batchId, 10);

  // Handlers for editing additional evidence items
  const handleEditItem = (item: AdditionalEvidenceItem) => {
    setEditingItemId(item.id);
    setEditingTitle(item.title);
    setEditingDescription(item.description);
    if (item.type === 'boxLink') {
      setEditingBoxLink(item.boxLink || '');
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingTitle('');
    setEditingDescription('');
    setEditingBoxLink('');
  };

  const isValidBoxUrl = (url: string): boolean => {
    const trimmed = url.trim();
    return trimmed.startsWith('https://cheplapharm.app.box.com/') ||
           trimmed.startsWith('https://app.box.com/');
  };

  const canSaveEdit = (): boolean => {
    if (!editingTitle.trim()) return false;
    const editingItem = additionalEvidenceItems?.find(item => item.id === editingItemId);
    if (editingItem?.type === 'boxLink' && !isValidBoxUrl(editingBoxLink)) return false;
    return true;
  };

  const handleSaveEdit = () => {
    if (!editingItemId || !setAdditionalEvidenceItems) return;
    if (!canSaveEdit()) return;

    setAdditionalEvidenceItems(prev => prev.map(item =>
      item.id === editingItemId
        ? {
            ...item,
            title: editingTitle.trim(),
            description: editingDescription.trim(),
            boxLink: item.type === 'boxLink' ? editingBoxLink.trim() : item.boxLink
          }
        : item
    ));
    handleCancelEdit();
  };

  const handleRemoveItem = (itemId: string) => {
    if (!setAdditionalEvidenceItems) return;
    setAdditionalEvidenceItems(prev => prev.filter(item => item.id !== itemId));
  };

  // Save progress handler
  const handleSaveProgress = async (): Promise<boolean> => {
    if (!sessionId || isNaN(batchNumber)) {
      console.error('[ConfirmationPage] Cannot save - missing sessionId or invalid batchId');
      setSaveError('Cannot save - missing session information');
      return false;
    }

    startProgressOperation?.('confirmationPage_save', CONFIRMATION_PAGE_SAVE_STEPS, 'Saving QP Submission');
    advanceProgressStep?.('Preparing to save...');
    blockNavigation?.('Saving QP Submission...');
    setSaving(true);
    setSaveError('');

    try {
      advanceProgressStep?.('Building save payload...');
      const pageData: QPReviewData = {
        fieldOverrides: fieldOverrides,
        complianceOverrides: complianceOverrides,
        autoPopulatedData: effectiveData,
        additionalEvidenceItems: additionalEvidenceItems?.map(item => ({
          id: item.id,
          type: item.type,
          title: item.title,
          description: item.description,
          createdAt: item.createdAt,
          fileName: item.fileName,
          fileSize: item.fileSize,
          uploadTime: item.uploadTime,
          boxFileId: item.boxFileId,
          blobName: item.blobName,
          url: item.url,
          boxLink: item.boxLink,
        })),
        cpInternalDocs: cpDocsEditedByQP ? cpInternalDocs : undefined,
        batchReleaseSite: batchReleaseSiteText || undefined,
        batchReleaseSiteConfirmed: batchReleaseSiteConfirmed,
        batchReleaseSiteCheplapharmConfirmed: batchReleaseSiteCheplapharmConfirmed,
        selectedReleaseSites: selectedReleaseSites.length > 0 ? selectedReleaseSites : undefined,
        // Serialization & Aggregation (editable on QP Submission)
        serializationRequirement: serializationRequirement || undefined,
        aggregationRequirement: aggregationRequirement || undefined,
        savedAt: new Date().toISOString(),
      };

      advanceProgressStep?.('Saving to database...');
      // Primary save: Neo4j (qp_review page)
      const response = await apiClient.saveReview({
        batchId: batchId,
        sessionId,
        pageName: 'qp_review',
        pageData,
        status: 'draft',
      });

      if (response.warnings && response.warnings.length > 0) {
        console.warn('[ConfirmationPage] Backend validation warnings:', response.warnings);
        setSaveError(`Warning: ${response.warnings.join('; ')}`);
      }

      advanceProgressStep?.('Updating session metadata...');
      // Secondary save: session audit log (non-critical)
      try {
        await sessionClient.logSave(
          batchId,
          sessionId,
          'qp-review'
        );
      } catch (auditError) {
        console.error('[ConfirmationPage] Audit log FAILED:', auditError);
      }

      // Tertiary save: session metadata (non-critical)
      try {
        await sessionClient.updateSession(sessionId, {
          batchId: batchId,
          currentPage: 'qp_review',
          lastSavedAt: new Date(),
          status: SessionStatus.InProgress,
        });
      } catch (sessionError) {
        console.error('[ConfirmationPage] session updateSession FAILED:', sessionError);
        if (!response.warnings || response.warnings.length === 0) {
          setSaveError('Warning: Session metadata save failed, but progress is saved.');
        }
      }

      advanceProgressStep?.('Save complete');
      setLastSaved(new Date());
      unblockNavigation?.();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[ConfirmationPage] Save failed:', error);
      setSaveError(`Failed to save progress: ${errorMessage}`);
      unblockNavigation?.(`Save failed: ${errorMessage}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Load saved state on mount or session change
  useEffect(() => {
    let isMounted = true;

    const loadSavedState = async () => {
      // Block navigation while loading (with progress tracking)
      startProgressOperation?.('confirmationPage_load', CONFIRMATION_PAGE_LOAD_STEPS, 'Loading QP Submission');
      advanceProgressStep?.('Preparing...');
      blockNavigation?.('Loading QP Submission...');

      // If sessionId is missing, there's no session to load from - show form
      if (!sessionId) {
        setLoading(false);
        unblockNavigation?.();
        return;
      }

      // If sessionId is provided but batchId is missing, wait for batchId (resuming session)
      if (!batchId) {
        // Keep loading=true - batchId may arrive on re-render
        return;
      }

      // If batchId is provided but can't be parsed as number, skip loading (test case or invalid ID)
      if (isNaN(batchNumber)) {
        setLoading(false);
        unblockNavigation?.();
        return;
      }

      // Check for session change
      const sessionHasChanged =
        previousSessionIdRef.current !== null &&
        previousSessionIdRef.current !== sessionId;

      previousSessionIdRef.current = sessionId;

      // Set loading state to true when starting to load (ensures loading indicator shows
      // when navigating back to this page, not just on initial mount)
      if (isMounted) {
        setLoading(true);
      }

      try {
        advanceProgressStep?.('Loading saved review...');
        const savedReview = await apiClient.getSavedReview(batchId, sessionId);

        if (!isMounted) return;

        advanceProgressStep?.('Applying field overrides...');
        // Detect if this is a cloned session
        if (savedReview?.clonedFromSessionId) {
          setIsClonedSession(true);
        }

        let cpDocsRestoredFromQP = false;
        let batchReleaseSiteConfirmedRestoredFromQP = false;
        let batchReleaseSiteCheplapharmRestoredFromQP = false;
        let selectedReleaseSitesRestoredFromQP = false;
        let restoredPackagingFromSave = false;
        let restoredMaterialsUsedFromSave = false;

        // Merge fieldOverrides from both brrForm (FormPage) and qpReview (QP Submission)
        // brrForm is base layer, qpReview takes precedence
        const brrFormData = savedReview?.brrForm as BRRFormData | undefined;
        const brrFormFieldOverrides = brrFormData?.fieldOverrides || {};
        const qpReviewFieldOverrides = savedReview?.qpReview?.fieldOverrides || {};
        const mergedFieldOverrides = { ...brrFormFieldOverrides, ...qpReviewFieldOverrides };
        if (Object.keys(mergedFieldOverrides).length > 0) {
          setFieldOverrides(mergedFieldOverrides);
        }

        // Merge complianceOverrides from both brrForm (FormPage) and qpReview (QP Submission)
        // brrForm is base layer, qpReview takes precedence
        const brrFormComplianceOverrides = brrFormData?.complianceOverrides || {};
        const qpReviewComplianceOverrides = savedReview?.qpReview?.complianceOverrides || {};
        const mergedComplianceOverrides = { ...brrFormComplianceOverrides, ...qpReviewComplianceOverrides };
        if (Object.keys(mergedComplianceOverrides).length > 0) {
          setComplianceOverrides(mergedComplianceOverrides);
        }

        if (savedReview && savedReview.qpReview) {
          const qpData = savedReview.qpReview;

          // Note: fieldOverrides and complianceOverrides are merged from brrForm + qpReview above
          advanceProgressStep?.('Processing form data...');

          // Restore additional evidence items (unified format)
          if (qpData.additionalEvidenceItems && qpData.additionalEvidenceItems.length > 0 && setAdditionalEvidenceItems) {
            setAdditionalEvidenceItems(qpData.additionalEvidenceItems as AdditionalEvidenceItem[]);
          }
          // Backward compatibility: migrate old format to new unified format
          else if ((qpData.additionalEvidenceDocuments || qpData.additionalEvidenceEntries) && setAdditionalEvidenceItems) {
            const migratedItems: AdditionalEvidenceItem[] = [];

            // Migrate old documents (PDFs)
            qpData.additionalEvidenceDocuments?.forEach(doc => {
              migratedItems.push({
                id: doc.id,
                type: 'pdf',
                title: doc.name,
                description: '',
                createdAt: doc.uploadTime || new Date().toISOString(),
                fileName: doc.name,
                fileSize: doc.size,
                uploadTime: doc.uploadTime,
                blobName: doc.blobName,
                url: doc.url,
              });
            });

            // Migrate old entries (Box links)
            qpData.additionalEvidenceEntries?.forEach(entry => {
              migratedItems.push({
                id: entry.id,
                type: 'boxLink',
                title: entry.title,
                description: entry.description,
                createdAt: entry.createdAt,
                boxLink: entry.boxLink,
              });
            });

            if (migratedItems.length > 0) {
              setAdditionalEvidenceItems(migratedItems);
            }
          }

          // Restore QP-edited CP Internal Documents
          if (qpData.cpInternalDocs) {
            setCpInternalDocs(qpData.cpInternalDocs);
            setCpDocsEditedByQP(true);
            cpDocsRestoredFromQP = true;
          }

          // Migrate legacy qpAddedCCs into manuallyAddedCCs (CC search moved to FormPage)
          if (qpData.qpAddedCCs && qpData.qpAddedCCs.length > 0) {
            const legacyCCs = qpData.qpAddedCCs.filter((cc: any) => cc != null);
            if (legacyCCs.length > 0) {
              setManuallyAddedCCs(prev => {
                const existingIds = new Set(prev.map((c: any) => c.stableId || c.actionId));
                const newCCs = legacyCCs.filter((cc: any) => !existingIds.has(cc.stableId) && !existingIds.has(cc.actionId));
                return [...prev, ...newCCs];
              });
            }
          }

          // Restore packaging materials from qpReview (highest priority)
          if (qpData.packagingMaterials && qpData.packagingMaterials.length > 0) {
            const validMaterials = qpData.packagingMaterials.filter((m: any) => m != null);
            setPackagingMaterials(validMaterials);
            restoredPackagingFromSave = true;
          }

          // Restore materials used from qpReview (highest priority)
          if (qpData.materialsUsed && (qpData.materialsUsed as MaterialUsedEntry[]).length > 0) {
            const validMaterials = (qpData.materialsUsed as MaterialUsedEntry[]).filter((m: any) => m != null);
            setMaterialsUsed(validMaterials);
            restoredMaterialsUsedFromSave = true;
          }

          // Restore GMP Annex 16 batch release site (QP override)
          if (qpData.batchReleaseSite !== undefined) {
            setBatchReleaseSiteText(qpData.batchReleaseSite);
          }
          if (qpData.batchReleaseSiteConfirmed !== undefined) {
            setBatchReleaseSiteConfirmed(qpData.batchReleaseSiteConfirmed);
            batchReleaseSiteConfirmedRestoredFromQP = true;
          }
          if (qpData.batchReleaseSiteCheplapharmConfirmed !== undefined) {
            setBatchReleaseSiteCheplapharmConfirmed(qpData.batchReleaseSiteCheplapharmConfirmed);
            batchReleaseSiteCheplapharmRestoredFromQP = true;
          }
          if (qpData.selectedReleaseSites !== undefined) {
            setSelectedReleaseSites(qpData.selectedReleaseSites);
            selectedReleaseSitesRestoredFromQP = true;
          }

          // Restore auto-populated data (for cloned BRRs when data prop is empty)
          if (qpData.autoPopulatedData) {
            setSavedAutoPopulatedData(qpData.autoPopulatedData);
          }

          if (qpData.savedAt) {
            setLastSaved(new Date(qpData.savedAt));
          }
        }

        // Also load brrForm data for CC/QE modifications and additional fields
        if (savedReview && savedReview.brrForm) {
          const brrData = savedReview.brrForm as BRRFormData;

          // Restore CC/QE modification state
          if (brrData.removedCCIds && brrData.removedCCIds.length > 0) {
            setRemovedCCIds(new Set(brrData.removedCCIds));
          }
          if (brrData.manuallyAddedCCs && brrData.manuallyAddedCCs.length > 0) {
            const validCCs = brrData.manuallyAddedCCs.filter((cc: any) => cc != null);
            setManuallyAddedCCs(validCCs);
          }
          if (brrData.removedQEIds && brrData.removedQEIds.length > 0) {
            setRemovedQEIds(new Set(brrData.removedQEIds));
          }
          if (brrData.manuallyAddedQEs && brrData.manuallyAddedQEs.length > 0) {
            const validQEs = brrData.manuallyAddedQEs.filter((qe: any) => qe != null);
            setManuallyAddedQEs(validQEs);
          }

          // Restore additional FormPage fields
          if (brrData.changeControlNotes && brrData.changeControlNotes.length > 0) {
            setChangeControlNotes(brrData.changeControlNotes);
          }
          if (brrData.individualCCNotes && Object.keys(brrData.individualCCNotes).length > 0) {
            setIndividualCCNotes(brrData.individualCCNotes);
          }
          if (brrData.countrySpecificRequirements) {
            setCountrySpecificRequirements(brrData.countrySpecificRequirements);
          }
          if (brrData.brrCreationBasis && brrData.brrCreationBasis.length > 0) {
            setBrrCreationBasis(brrData.brrCreationBasis);
          }
          if (brrData.cpInternalDocs && brrData.cpInternalDocs.length > 0 && !cpDocsRestoredFromQP) {
            setCpInternalDocs(brrData.cpInternalDocs);
          }
          if (brrData.nextPeriodicReview) {
            setNextPeriodicReview(brrData.nextPeriodicReview);
          }
          if (brrData.documentSectionData) {
            setDocumentSectionData(brrData.documentSectionData);
          }
          if (brrData.customDocumentSections) {
            setCustomDocSections(brrData.customDocumentSections);
          }
          if (brrData.boxFolderUrls) {
            setBoxFolderUrls(brrData.boxFolderUrls);
          }
          // Restore saved packaging materials from brrForm (if not already restored from qpReview)
          // Skip re-extraction when restoring saved data to preserve user edits
          const savedPackagingMats = brrData.packagingMaterials as PackagingMaterialEntry[] | undefined;
          if (savedPackagingMats && savedPackagingMats.length > 0 && !restoredPackagingFromSave) {
            setPackagingMaterials(savedPackagingMats);
            restoredPackagingFromSave = true;
          }

          // Restore materials used from brrForm (if not already restored from qpReview)
          const savedMaterialsUsed = brrData.materialsUsed as MaterialUsedEntry[] | undefined;
          if (savedMaterialsUsed && savedMaterialsUsed.length > 0 && !restoredMaterialsUsedFromSave) {
            setMaterialsUsed(savedMaterialsUsed);
            restoredMaterialsUsedFromSave = true;
          }

          // Note: fieldOverrides are merged from brrForm + qpReview above (before this block)

          // Restore GMP Annex 16 batch release site from brrForm (if not already set by qpReview)
          if (brrData.batchReleaseSite) {
            setBatchReleaseSiteText(prev => prev || brrData.batchReleaseSite || '');
          }
          if (!batchReleaseSiteConfirmedRestoredFromQP && brrData.batchReleaseSiteConfirmed !== undefined) {
            setBatchReleaseSiteConfirmed(brrData.batchReleaseSiteConfirmed);
          }
          if (!batchReleaseSiteCheplapharmRestoredFromQP && brrData.batchReleaseSiteCheplapharmConfirmed !== undefined) {
            setBatchReleaseSiteCheplapharmConfirmed(brrData.batchReleaseSiteCheplapharmConfirmed);
          }
          if (!selectedReleaseSitesRestoredFromQP && brrData.selectedReleaseSites !== undefined) {
            setSelectedReleaseSites(brrData.selectedReleaseSites);
          }
        }

        // Load document_validation data for Serialization & Aggregation section
        // QP Review values take priority (if QP edited them)
        const qpSerialization = savedReview?.qpReview?.serializationRequirement;
        const qpAggregation = savedReview?.qpReview?.aggregationRequirement;

        if (savedReview && savedReview.documentValidation) {
          const docVal = savedReview.documentValidation;
          // QP edits take priority over documentValidation values
          setSerializationRequirement(qpSerialization || docVal.serializationRequirement || '');
          setAggregationRequirement(qpAggregation || docVal.aggregationRequirement || '');
          if (docVal.tracelinkGtin) {
            setTracelinkGtin(docVal.tracelinkGtin);
          }
          if (docVal.tracelinkQuantity) {
            setTracelinkQuantity(docVal.tracelinkQuantity);
          }
          if (docVal.tracelinkHierarchyLevels) {
            setTracelinkHierarchyLevels(docVal.tracelinkHierarchyLevels);
          }

          // Extract packaging materials from tablesSnapshot
          // Skip if we already restored saved data (which contains user edits)
          // Note: keys may be snake_case or camelCase depending on whether transformKeys() ran
          if (!restoredPackagingFromSave && docVal.tablesSnapshot?.tables) {
            const extractedMaterials: PackagingMaterialEntry[] = [];
            const tables = docVal.tablesSnapshot.tables;

            const SUBTYPE_LABELS: Record<string, string> = {
              LABEL: 'Labels', FOIL: 'Foils', LEAFLET: 'Leaflet',
              FOLDING_BOX: 'Folding Box', PRINTED_PACKAGING: 'Printed Packaging Materials',
              PACKAGING: 'Packaging',
            };
            Object.entries(tables).forEach(([tableKey, tableData]) => {
              if (tableKey.startsWith('PACKAGING_MATERIALS') && tableData) {
                const table = tableData as unknown as Record<string, unknown>;
                const rows = (table.rows || []) as Array<Record<string, string | null>>;
                const getValue = (snakeKey: string, camelKey: string) => {
                  for (const r of rows) {
                    const rKey = r.extraction_key ?? r.extractionKey ?? '';
                    if (rKey === snakeKey || rKey === camelKey) {
                      return r.extracted_value ?? r.extractedValue ?? '';
                    }
                  }
                  return '';
                };
                const subtypeMatch = tableKey.match(/^PACKAGING_MATERIALS__(.+?)(?:_\d+)?$/);
                const subtypeFallback = subtypeMatch ? (SUBTYPE_LABELS[subtypeMatch[1]] || subtypeMatch[1]) : '';
                extractedMaterials.push({
                  id: `extracted_${tableKey}`,
                  componentType: getValue('packaging_type', 'packagingType') || subtypeFallback,
                  cpMaterialNumber: getValue('kau_material_number', 'kauMaterialNumber'),
                  cmoMaterialNumber: getValue('cmo_material_number', 'cmoMaterialNumber'),
                  versionNumber: getValue('kau_version', 'kauVersion'),
                  source: 'extracted',
                  filename: (table.filename as string) || undefined,
                });
              }
            });

            if (extractedMaterials.length > 0) {
              setPackagingMaterials(extractedMaterials);
            }
          }
        }
      } catch (error) {
        console.error('[ConfirmationPage] Failed to load saved state:', error);
      } finally {
        advanceProgressStep?.('Finalizing...');
        if (isMounted) {
          setLoading(false);
          unblockNavigation?.();
        }
      }
    };

    loadSavedState();

    return () => {
      isMounted = false;
    };
  }, [sessionId, batchNumber, apiClient, setFieldOverrides, setAdditionalEvidenceItems, startProgressOperation, advanceProgressStep]);

  // Timeout loading state if batchId never arrives (safety net to prevent infinite loading)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && sessionId && !batchId) {
        setLoading(false);
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [sessionId, batchId, loading]);

  // Use data prop if available, otherwise fall back to saved auto-populated data (for cloned BRRs)
  const effectiveData = data || savedAutoPopulatedData;

  // Show loading if we have valid params but data hasn't loaded yet
  const isDataLoading = loading || (!effectiveData && sessionId && batchId && !isNaN(batchNumber));

  // Compute effective change controls: auto-populated minus removed plus manually added plus QP-added
  const effectiveCCs = React.useMemo(() => {
    const autoPopulatedCCs = effectiveData?.regulatoryQMS?.changeControls?.value || [];
    const filteredCCs = autoPopulatedCCs.filter((cc: any) => {
      if (!cc) return false; // Skip null/undefined elements
      const ccId = cc.stableId || cc.id || cc.actionId;
      return !removedCCIds.has(ccId);
    });
    // Filter all sources to ensure no nulls slip through
    return [...filteredCCs, ...manuallyAddedCCs].filter((cc: any) => cc != null);
  }, [effectiveData, removedCCIds, manuallyAddedCCs]);

  // Compute effective quality events: auto-populated minus removed plus manually added
  const effectiveQEs = React.useMemo(() => {
    const autoPopulatedQEs = effectiveData?.regulatoryQMS?.deviations?.value || [];
    const filteredQEs = autoPopulatedQEs.filter((qe: any) => {
      if (!qe) return false; // Skip null/undefined elements
      const qeId = qe.stableId || qe.id;
      return !removedQEIds.has(qeId);
    });
    // Filter all sources to ensure no nulls slip through
    return [...filteredQEs, ...manuallyAddedQEs].filter((qe: any) => qe != null);
  }, [effectiveData, removedQEIds, manuallyAddedQEs]);

  // Helper function to get the display value - uses override if exists, otherwise original
  // For manufacturer fields, checks for ManualEntry companion field when "Value not in list" is selected
  const getDisplayValue = (fieldId: string, originalValue: string | boolean) => {
    const override = fieldOverrides?.[fieldId];
    if (override && typeof override === 'object' && 'value' in override) {
      // Check for manual entry pattern on manufacturer fields
      if (MANUFACTURER_FIELDS.has(fieldId) && override.value === NOT_FOUND_VALUE) {
        const manualEntryKey = `${fieldId}ManualEntry`;
        const manualEntry = fieldOverrides?.[manualEntryKey];
        if (manualEntry && typeof manualEntry === 'object' && 'value' in manualEntry && manualEntry.value) {
          return `(Manual) ${manualEntry.value}`;
        }
      }
      return override.value ?? originalValue;
    }
    return originalValue;
  };

  const ComplianceDropdown = ({ fieldId }: { fieldId: string }) => {
    const complianceValue = complianceOverrides[fieldId] || '';
    const complianceOptions = getComplianceOptionsForField(fieldId);
    const isNegativeCompliance = complianceValue.includes('does_not') || complianceValue === 'non_compliant' || complianceValue === 'under_review' || complianceValue === 'open';
    const isPositiveCompliance = !isNegativeCompliance && (complianceValue.includes('compliant') || complianceValue.includes('complies') || complianceValue === 'current' || complianceValue === 'closed' || complianceValue === 'created');
    const isNeutral = complianceValue === 'na';
    return (
      <div className="mt-2 pt-2 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Compliance:</span>
          <select
            value={complianceValue}
            onChange={(e) => setComplianceOverrides(prev => ({ ...prev, [fieldId]: e.target.value }))}
            className={`px-2 py-1 text-xs border rounded font-medium focus:outline-none focus:ring-1 ${
              isPositiveCompliance
                ? 'border-green-300 bg-green-50 text-green-700 focus:ring-green-200'
                : isNegativeCompliance
                ? 'border-red-300 bg-red-50 text-red-700 focus:ring-red-200'
                : isNeutral
                ? 'border-gray-300 bg-gray-100 text-gray-600 focus:ring-gray-200'
                : 'border-gray-300 bg-white text-gray-500 focus:ring-gray-200'
            }`}
          >
            <option value="" className="bg-white text-gray-500">Not Set</option>
            {complianceOptions.map(option => (
              <option key={option.value} value={option.value} className="bg-white text-gray-900">
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // Helper component to display a field with "Edited" indicator and compliance status
  const DisplayField = ({ fieldId, label, originalValue, showCompliance = true }: { fieldId: string; label: string; originalValue: string | boolean; showCompliance?: boolean }) => {
    const isEdited = fieldOverrides[fieldId];
    const displayValue = getDisplayValue(fieldId, originalValue);
    const validationNotes = fieldOverrides[fieldId]?.validationNotesArray || [];
    const [isEditing, setIsEditing] = useState(false);
    const [editedValue, setEditedValue] = useState(String(displayValue));
    const [currentNoteText, setCurrentNoteText] = useState('');
    const complianceValue = complianceOverrides[fieldId] || '';
    const complianceOptions = getComplianceOptionsForField(fieldId);

    const handleSave = async () => {
      const newNote: ValidationNote = {
        text: currentNoteText,
        timestamp: new Date().toISOString()
      };
      const updatedNotes = currentNoteText.trim() ? [...validationNotes, newNote] : validationNotes;

      setFieldOverrides(prev => ({
        ...prev,
        [fieldId]: {
          value: editedValue,
          originalValue: originalValue,
          validationNotesArray: updatedNotes
        }
      }));
      // Small delay to ensure state updates are processed before save
      await new Promise(resolve => setTimeout(resolve, 10));
      const success = await handleSaveProgress();
      if (success) {
        setIsEditing(false);
        setCurrentNoteText('');
      }
    };

    const handleCancel = () => {
      setEditedValue(String(displayValue));
      setIsEditing(false);
      setCurrentNoteText('');
    };

    const handleComplianceChange = (newValue: string) => {
      setComplianceOverrides(prev => ({
        ...prev,
        [fieldId]: newValue
      }));
    };

    // Determine styling based on compliance value
    const isNegativeCompliance = complianceValue.includes('does_not') || complianceValue === 'non_compliant' || complianceValue === 'under_review' || complianceValue === 'open';
    const isPositiveCompliance = !isNegativeCompliance && (complianceValue.includes('compliant') || complianceValue.includes('complies') || complianceValue === 'current' || complianceValue === 'closed' || complianceValue === 'created');
    const isNeutral = complianceValue === 'na';

    return (
      <div className={`p-3 rounded border-2 min-w-0 ${isEdited ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-gray-500 break-words">{label}</div>
          <div className="flex items-center gap-2">
            {isEdited && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">Edited</span>
            )}
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Edit
              </button>
            )}
          </div>
        </div>
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <textarea
              value={currentNoteText}
              onChange={(e) => setCurrentNoteText(e.target.value)}
              placeholder="Add validation note (optional)"
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
              rows={2}
            />
            <div className="flex gap-2">
              {ENABLE_SAVE_FUNCTIONALITY && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`px-2 py-1 text-white text-xs rounded flex items-center gap-1 ${
                    saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin" size={12} />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              )}
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{String(displayValue)}</div>
        )}
        {/* Compliance Status - Field-specific options */}
        {showCompliance && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Compliance:</span>
              <select
                value={complianceValue}
                onChange={(e) => handleComplianceChange(e.target.value)}
                className={`px-2 py-1 text-xs border rounded font-medium focus:outline-none focus:ring-1 ${
                  isPositiveCompliance
                    ? 'border-green-300 bg-green-50 text-green-700 focus:ring-green-200'
                    : isNegativeCompliance
                    ? 'border-red-300 bg-red-50 text-red-700 focus:ring-red-200'
                    : isNeutral
                    ? 'border-gray-300 bg-gray-100 text-gray-600 focus:ring-gray-200'
                    : 'border-gray-300 bg-white text-gray-500 focus:ring-gray-200'
                }`}
              >
                <option value="" className="bg-white text-gray-500">Not Set</option>
                {complianceOptions.map(option => (
                  <option key={option.value} value={option.value} className="bg-white text-gray-900">
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        {validationNotes.length > 0 && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-1 mb-1">
              <div className="p-1 bg-indigo-100 rounded">
                <FileText size={12} className="text-indigo-600" />
              </div>
              <span className="text-xs font-bold text-indigo-900 uppercase tracking-wide">Validation Notes</span>
              <span className="text-xs text-gray-500">({validationNotes.length})</span>
            </div>
            {validationNotes.map((note, index) => (
              <div key={index} className="p-2 bg-gradient-to-r from-indigo-50 to-green-50 border-2 border-indigo-300 rounded">
                <div className="flex items-center gap-1 mb-1 text-xs text-indigo-600">
                  <Clock size={10} />
                  <span>{formatDateTime(note.timestamp)}</span>
                </div>
                <div className="text-xs text-gray-800 whitespace-pre-wrap bg-white/60 p-1.5 rounded border border-indigo-200">
                  {note.text}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // State for Box upload result
  const [boxUploadResult, setBoxUploadResult] = useState<{ filename: string; boxFolderId: string } | null>(null);

  // Box folder ID from the form-data API (fetched independently to avoid timing issues
  // where FormPage auto-saved before the orchestrator finished creating the Box folder)
  const [fetchedBoxFolderId, setFetchedBoxFolderId] = useState<string | null>(null);

  const inspectionLotForBoxFetch = effectiveData?.sapIdentityBatch?.inspectionLotNumber?.value || inspectionLotIdProp;
  useEffect(() => {
    if (effectiveData?.boxArchive?.folder_id || fetchedBoxFolderId || !inspectionLotForBoxFetch) return;
    let cancelled = false;
    apiClient.getFormData(String(inspectionLotForBoxFetch)).then(resp => {
      if (!cancelled && resp.boxArchive?.folder_id) {
        setFetchedBoxFolderId(resp.boxArchive.folder_id);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [apiClient, inspectionLotForBoxFetch, effectiveData?.boxArchive?.folder_id, fetchedBoxFolderId]);

  const boxFolderId = effectiveData?.boxArchive?.folder_id || fetchedBoxFolderId || null;

  // Helper to perform Word export to Box folder
  const performWordExportToBox = async () => {
    const inspectionLotIds = selectedLotIds && selectedLotIds.length > 0
      ? selectedLotIds.join(',')
      : effectiveData?.sapIdentityBatch?.inspectionLotNumber?.value || inspectionLotIdProp || batchId;

    const productName = effectiveData?.productMarketData?.productName?.value || undefined;

    if (boxFolderId) {
      const result = await apiClient.exportWordToBox(
        String(inspectionLotIds),
        boxFolderId,
        sessionId || undefined,
        productName,
        false,
        brrLevel || undefined,
      );
      setBoxUploadResult({ filename: result.filename, boxFolderId: result.box_folder_id });
    } else {
      const blob = await apiClient.downloadWordExport(
        String(inspectionLotIds),
        sessionId || undefined,
        productName,
        false,
        brrLevel || undefined,
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `BRR_${displayBatchNumber}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadWord = async () => {
    setSavingBeforeDownload('word');
    setBoxUploadResult(null);

    try {
      const saveSuccess = await handleSaveProgress();

      if (!saveSuccess) {
        const proceedAnyway = window.confirm(
          'Failed to save your changes before exporting.\n\nDo you want to export anyway? Your recent changes may not be included in the export.'
        );
        if (!proceedAnyway) {
          setSavingBeforeDownload(null);
          return;
        }
      }

      await performWordExportToBox();
    } catch (error) {
      console.error('Failed to export Word document:', error);
      alert('Failed to export Word document. Please try again.');
    } finally {
      setSavingBeforeDownload(null);
    }
  };

  const displayBatchNumber = batchId ? formatBatchId(batchId) : 'No Data';
  const displayProductName = effectiveData?.productMarketData?.productName?.value || 'No Data';
  const displayCountry = effectiveData?.productMarketData?.targetCountry?.value || 'No Data';

  // Scroll to top when component mounts (ensures user sees QP submission header first)
  React.useEffect(() => {
    // Aggressive scroll to top strategy to override any other scroll behavior
    const scrollToTop = () => {
      const headerElement = document.getElementById('qp-assignment-header');

      // Scroll window first
      window.scrollTo(0, 0);

      if (headerElement) {
        // Find the scrollable parent container (BRRApp's overflow-auto div)
        let scrollableParent = headerElement.parentElement;
        while (scrollableParent) {
          const overflowY = window.getComputedStyle(scrollableParent).overflowY;
          if (overflowY === 'auto' || overflowY === 'scroll') {
            // Found the scrollable container, scroll it to top immediately
            scrollableParent.scrollTop = 0;
            break;
          }
          scrollableParent = scrollableParent.parentElement;
        }

        // Use scrollIntoView with instant behavior
        headerElement.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    };

    // Scroll multiple times to ensure it sticks
    // Immediate scroll
    scrollToTop();

    // After first paint
    requestAnimationFrame(() => {
      scrollToTop();
    });

    // After a short delay to override any late-rendering scroll
    const scrollTimer = setTimeout(() => {
      scrollToTop();
    }, 200);

    // Final scroll after everything settles
    const finalScrollTimer = setTimeout(() => {
      scrollToTop();
    }, 500);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(finalScrollTimer);
    };
  }, []);

  // Show full-page loading state while fetching saved data
  if (isDataLoading) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 relative mb-6">
            {/* Outer ring */}
            <div className="absolute inset-0 border-4 border-green-200 rounded-full"></div>
            {/* Spinning segment */}
            <div
              className="absolute inset-0 border-4 border-transparent border-t-green-600 rounded-full animate-spin"
              style={{ animationDuration: '1s' }}
            ></div>
            {/* Inner icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <FileText className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading QP Review</h3>
          <p className="text-sm text-gray-500">Retrieving saved submission data...</p>
        </div>
      </div>
    );
  }

  // For cloned sessions, require data - don't allow empty state
  if (isClonedSession && !effectiveData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-3" />
          <p className="text-red-600 font-semibold">Error loading cloned BRR data</p>
          <p className="text-gray-600 text-sm mt-2">The cloned BRR is missing required data. Please try cloning again.</p>
        </div>
      </div>
    );
  }

  return (
    <div id="qp-assignment-content" className="space-y-6">
      {/* Header */}
      <div id="qp-assignment-header" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-bold text-gray-900">Submit Batch Record Review for QP Approval</h2>
            </div>
            <div className="space-y-1">
              <div className="text-base font-semibold text-gray-900 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }} title={displayProductName}>{displayProductName}</div>
              <div className="flex items-center gap-4 text-gray-500 text-sm">
                <span className="flex items-center gap-1"><Package size={14} /> {displayBatchNumber}</span>
                <span className="flex items-center gap-1"><Globe size={14} /> {displayCountry}</span>
                <span className="flex items-center gap-1"><Layers size={14} /> BRR {formatBrrLevel(brrLevel)}</span>
                <span className="flex items-center gap-1"><Clock size={14} /> Prepared: {formatDate(new Date())}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Prepared By</div>
            <div className="text-lg font-bold text-green-600">{userEmail}</div>
          </div>
        </div>
      </div>

      {/* Complete BRR Display (Read-Only) */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText size={18} className="text-blue-600" />
          Complete Batch Record Review
        </h3>
        <div className="space-y-6">
          {/* BRR Header Information */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">BRR Header & Product Identification</h4>
            <div className="grid grid-cols-2 gap-4">
              <DisplayField fieldId="productName" label="Product Name" originalValue={effectiveData?.productMarketData?.productName?.value || ''} />
              <DisplayField fieldId="packSize" label="Pack Size" originalValue={effectiveData?.productMarketData?.packSize?.value || ''} />
              <DisplayField fieldId="dosageForm" label="Dosage Form" originalValue={effectiveData?.productMarketData?.dosageForm?.value || ''} />
              <DisplayField fieldId="doseStrength" label="Dose Strength" originalValue={effectiveData?.productMarketData?.doseStrength?.value || ''} />
              <DisplayField fieldId="productNameGlobal" label="Product Name (Global)" originalValue={effectiveData?.productMarketData?.productNameGlobal?.value || ''} />
              <DisplayField fieldId="destinationCountry" label="Destination Country" originalValue={effectiveData?.productMarketData?.targetCountry?.value || ''} />
              <DisplayField fieldId="materialSKU" label="Material Number" originalValue={effectiveData?.productMarketData?.materialSKU?.value || ''} />
              <DisplayField fieldId="sapInternalBatch" label="SAP Internal Batch Number" originalValue={formatBatchId(effectiveData?.sapIdentityBatch?.sapInternalBatch?.value)} />
              <DisplayField fieldId="cmoFpBatchId" label="CMO FP Batch ID" originalValue={String(effectiveData?.sapIdentityBatch?.cmoFpBatchId?.value || '')} />
              <DisplayField
                fieldId="inspectionLotNumber"
                label={selectedLotIds && selectedLotIds.length > 1 ? "Inspection Lot Numbers" : "Inspection Lot Number"}
                originalValue={
                  selectedLotIds && selectedLotIds.length > 0
                    ? selectedLotIds.join(', ')
                    : effectiveData?.sapIdentityBatch?.inspectionLotNumber?.value || ''
                }
              />
              {indirectTransportLotId && (
                <DisplayField fieldId="indirectTransportLotId" label="Indirect Transport Lot ID" originalValue={indirectTransportLotId} />
              )}
              {/* Batch Quantity - shows per-lot breakdown when multiple lots */}
              {(() => {
                const sapLotQuantities = effectiveData?.sapIdentityBatch?.sapLotQuantities;
                const unit = effectiveData?.sapIdentityBatch?.unitOfMeasure?.value || '';
                const hasMultipleLots = sapLotQuantities && Array.isArray(sapLotQuantities) && sapLotQuantities.length > 1;

                if (hasMultipleLots) {
                  let totalQuantity = 0;
                  const perLotLines = sapLotQuantities.map((lq: { lotId: string; quantity: number | null }) => {
                    const qty = lq.quantity ?? 0;
                    totalQuantity += qty;
                    const formatted = formatNumber(String(qty));
                    return `${lq.lotId}: ${formatted}${unit ? ` ${unit}` : ''}`;
                  });
                  const totalFormatted = formatNumber(String(totalQuantity));
                  const displayValue = [
                    ...perLotLines,
                    `Total: ${totalFormatted}${unit ? ` ${unit}` : ''}`
                  ].join('\n');

                  return (
                    <div className="col-span-2">
                      <DisplayField
                        fieldId="batchQuantity"
                        label="Batch Quantity (SAP per Lot)"
                        originalValue={displayValue}
                      />
                    </div>
                  );
                }

                // Single lot - append unit to displayed value
                const formatted = formatNumber(effectiveData?.sapIdentityBatch?.batchQuantity?.value);
                const displayVal = unit && formatted ? `${formatted} ${unit}` : formatted;
                return (
                  <DisplayField
                    fieldId="batchQuantity"
                    label="Batch Quantity"
                    originalValue={displayVal}
                  />
                );
              })()}
              <DisplayField fieldId="deliveryType" label="Delivery Type" originalValue={effectiveData?.sapIdentityBatch?.deliveryType?.value || ''} />
              <DisplayField fieldId="batchManufactureDate" label="Batch Manufacture Date" originalValue={effectiveData?.sapIdentityBatch?.batchManufactureDate?.value || ''} />
              <DisplayField fieldId="batchExpiryDate" label="Batch Expiry Date" originalValue={effectiveData?.sapIdentityBatch?.batchExpiryDate?.value || ''} />
              <DisplayField fieldId="calculatedExpiryDate" label="Calculated Expiry Date" originalValue={effectiveData?.sapIdentityBatch?.calculatedExpiryDate?.value || ''} />
            </div>
          </div>

          {/* Materials Used */}
          {materialsUsed.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2 flex items-center gap-2">
                <Package size={14} className="text-purple-600" />
                Materials Used ({materialsUsed.length})
              </h4>
              <div className="grid grid-cols-2 gap-4">
                {materialsUsed.map((item) => (
                  <DisplayField
                    key={item.id || item.vendorBatchId}
                    fieldId={`materialsUsed_${item.id}`}
                    label={`${item.material || 'Material'}${item.source === 'manual' ? ' (manual)' : ''}`}
                    originalValue={String(item.vendorBatchId || '—')}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Supplier Status */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Supplier Status</h4>
            <div className="grid grid-cols-2 gap-4">
              <DisplayField fieldId="cmoName" label="CMO Name" originalValue={effectiveData?.cmoSupplierQual?.cmoName?.value || ''} />
              <DisplayField
                fieldId="validToDate"
                label="Valid To Date"
                originalValue={(() => {
                  // Get the selected supplier name (from fieldOverrides or the cmoName)
                  const selectedSupplierName = fieldOverrides.supplierName?.value || effectiveData?.cmoSupplierQual?.cmoName?.value;
                  if (!selectedSupplierName || selectedSupplierName === NOT_FOUND_VALUE) {
                    return '';
                  }
                  // Find the supplier in cmoNameSAPList to get its validToDate
                  const selectedSupplier = effectiveData?.cmoSupplierQual?.cmoNameSAPList?.find(
                    (s: { name: { value: string }; validToDate: { value: string } }) =>
                      s.name.value === selectedSupplierName
                  );
                  return selectedSupplier?.validToDate?.value || '';
                })()}
              />
              <DisplayField fieldId="cmoQualStatus" label="CMO Qualification Status" originalValue={effectiveData?.cmoSupplierQual?.cmoQualStatus?.value || ''} />
              <DisplayField fieldId="cmoComplianceReviewDate" label="CMO Compliance Review Date" originalValue={effectiveData?.cmoSupplierQual?.cmoComplianceReviewDate?.value || ''} />
              <DisplayField fieldId="qaaStatus" label="QAA Status" originalValue={effectiveData?.cmoSupplierQual?.qaaStatus?.value || ''} />
              <DisplayField fieldId="qaaValidity" label="QAA Validity" originalValue={effectiveData?.cmoSupplierQual?.qaaValidity?.value || ''} />
            </div>
          </div>

          {/* Approval Details */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Approval Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <DisplayField fieldId="marketAuthorisationNumber" label="Marketing Authorisation Number" originalValue={(Array.isArray(effectiveData?.approvalDetails?.marketAuthorisationNumber) ? effectiveData?.approvalDetails?.marketAuthorisationNumber?.map((a: any) => a?.value).filter(Boolean).join(', ') : effectiveData?.approvalDetails?.marketAuthorisationNumber?.value) || ''} />
              <DisplayField fieldId="authorisationStatus" label="Authorisation Status" originalValue={effectiveData?.approvalDetails?.authorisationStatus?.value || ''} />
              <DisplayField fieldId="validityPeriodEndValue" label="Validity Period End" originalValue={effectiveData?.approvalDetails?.validityPeriodEndValue?.value || ''} />
              <DisplayField fieldId="apiName" label="API Name" originalValue={effectiveData?.approvalDetails?.apiName?.map((a: any) => a?.value).filter(Boolean).join(', ') || ''} />
              <DisplayField fieldId="apiManufacturer" label="API Manufacturer" originalValue={effectiveData?.approvalDetails?.apiManufacturer?.map((a: any) => a?.value).filter(Boolean).join(', ') || ''} />
              <DisplayField fieldId="reportedShelfLife" label="Shelf life as packaged for sale (months)" originalValue={effectiveData?.approvalDetails?.reportedShelfLife?.value || ''} />
              <DisplayField fieldId="bulkManufacturer" label="Bulk Manufacturer" originalValue={effectiveData?.approvalDetails?.bulkManufacturer?.map((a: any) => a?.value).filter(Boolean).join(', ') || ''} />
              <DisplayField fieldId="qcTestingSite" label="QC Testing Site" originalValue={effectiveData?.approvalDetails?.qcTestingSite?.map((a: any) => a?.value).filter(Boolean).join(', ') || ''} />
              <DisplayField fieldId="primaryPackagingSite" label="Primary Packaging Site" originalValue={(Array.isArray(effectiveData?.approvalDetails?.primaryPackagingSite) ? effectiveData?.approvalDetails?.primaryPackagingSite?.map((a: any) => a?.value).filter(Boolean).join(', ') : effectiveData?.approvalDetails?.primaryPackagingSite?.value) || ''} />
              <DisplayField fieldId="secondaryPackagingSite" label="Secondary Packaging Site" originalValue={(Array.isArray(effectiveData?.approvalDetails?.secondaryPackagingSite) ? effectiveData?.approvalDetails?.secondaryPackagingSite?.map((a: any) => a?.value).filter(Boolean).join(', ') : effectiveData?.approvalDetails?.secondaryPackagingSite?.value) || ''} />
            </div>

            {/* GMP Annex 16: Batch Release Site Confirmation */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <h5 className="text-sm font-semibold text-gray-800 mb-3">
                Confirmation of Batch Release Site according to GMP Annex 16
              </h5>
              <div className="space-y-2">
                {/* Certification of batch by manufacturer */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="batchReleaseSiteConfirmedQP"
                    checked={batchReleaseSiteConfirmed}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setBatchReleaseSiteConfirmed(checked);
                      if (checked) {
                        setBatchReleaseSiteCheplapharmConfirmed(false);
                      }
                    }}
                    className="rounded cursor-pointer"
                  />
                  <label htmlFor="batchReleaseSiteConfirmedQP" className="text-sm text-gray-700 cursor-pointer">
                    Certification of batch by manufacturer
                  </label>
                </div>
                {/* Certification of batch by Cheplapharm */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="batchReleaseSiteCheplapharmConfirmedQP"
                    checked={batchReleaseSiteCheplapharmConfirmed}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setBatchReleaseSiteCheplapharmConfirmed(checked);
                      if (checked) {
                        setBatchReleaseSiteConfirmed(false);
                        setSelectedReleaseSites([]);
                      }
                    }}
                    className="rounded cursor-pointer"
                  />
                  <label htmlFor="batchReleaseSiteCheplapharmConfirmedQP" className="text-sm text-gray-700 cursor-pointer">
                    Certification of batch by Cheplapharm
                  </label>
                </div>

                {/* Show selectable release sites only when "by manufacturer" is checked */}
                {batchReleaseSiteConfirmed && (
                  <div className="mt-2">
                    {Array.isArray(effectiveData?.approvalDetails?.releaseSite) && (effectiveData?.approvalDetails?.releaseSite?.length ?? 0) > 0 ? (
                      <div className="p-3 bg-blue-50 rounded border border-blue-200">
                        <label className="block text-xs font-medium text-blue-700 mb-1">Release Site (from CARA)</label>
                        <div className="text-sm text-gray-800 space-y-1">
                          {effectiveData?.approvalDetails?.releaseSite?.map((site: any, idx: number) => (
                            <div key={idx} className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                id={`releaseSiteQP-${idx}`}
                                checked={selectedReleaseSites.includes(site?.value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedReleaseSites(prev => [...prev, site.value]);
                                  } else {
                                    setSelectedReleaseSites(prev => prev.filter(s => s !== site.value));
                                  }
                                }}
                                className="rounded cursor-pointer mt-0.5"
                              />
                              <label htmlFor={`releaseSiteQP-${idx}`} className="cursor-pointer text-left" style={{ overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>
                                {site?.value}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-amber-600 flex items-center gap-1">
                        <AlertCircle size={12} />
                        <span>No release site data in CARA</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Editable confirmation text */}
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Additional Notes / Confirmation Details
                </label>
                <textarea
                  value={batchReleaseSiteText}
                  onChange={(e) => setBatchReleaseSiteText(e.target.value)}
                  placeholder="Enter batch release site confirmation details, GMP compliance notes, or any additional information required for Annex 16 certification..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-y"
                />
              </div>
            </div>

            {/* Packaging Materials (KAU Data) - read-only display (managed on FormPage) */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-medium text-gray-700">Packaging Materials Used ({packagingMaterials.length})</h5>
              </div>

              {packagingMaterials.length === 0 ? (
                <div className="p-3 bg-gray-50 rounded border border-gray-200 text-center text-sm text-gray-500">
                  No packaging materials data. Packaging materials are managed on the Form page.
                </div>
              ) : (
                <div className="space-y-2">
                  {packagingMaterials.map((material) => (
                    <div
                      key={material.id}
                      className={`p-2 rounded border ${material.source === 'manual' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900 text-sm">{material.componentType || 'Unknown Type'}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${material.source === 'manual' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {material.source === 'manual' ? 'Manual' : 'Extracted'}
                          </span>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-600">
                          {material.cpMaterialNumber && (
                            <span><span className="text-gray-500">Artworks Mat #:</span> {material.cpMaterialNumber}</span>
                          )}
                          {material.versionNumber && (
                            <span><span className="text-gray-500">Artworks Ver #:</span> {material.versionNumber}</span>
                          )}
                          {material.cmoMaterialNumber && (
                            <span><span className="text-gray-500">CMO:</span> {material.cmoMaterialNumber}</span>
                          )}
                          {material.filename && (
                            <span className="text-gray-400">({material.filename})</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Storage & Country Requirements */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Storage Information & Country Requirements</h4>
            <div className="grid grid-cols-2 gap-4">
              <DisplayField fieldId="targetStorageLocation" label="Target Storage Location" originalValue={effectiveData?.sapIdentityBatch?.targetStorageLocation?.value || ''} />
              <DisplayField fieldId="storageStatement" label="Storage Statement" originalValue={effectiveData?.productMarketData?.storageStatement?.value || ''} />
              <DisplayField fieldId="countryValidationStatus" label="Validation Status" originalValue={effectiveData?.regulatoryQMS?.countryValidationStatus?.value || ''} />
            </div>
          </div>

          {/* Quality & Compliance — Change Controls */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Quality Management System Compliance — Change Controls</h4>

            {effectiveCCs.length > 0 && effectiveCCs.map((cc: any) => {
                const ccId = cc.stableId || cc.id || cc.actionId;
                const ccNotes = individualCCNotes[ccId] || [];
                return (
                  <div key={ccId} className="p-3 rounded border bg-gray-50 border-gray-200" style={{ overflow: 'hidden' }}>
                    <span className="font-semibold text-gray-900 text-sm">{cc.actionId || cc.id}</span>
                    {ccNotes.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="text-xs font-medium text-gray-700">Validation Notes:</div>
                        {ccNotes.map((note: any, noteIndex: number) => (
                          <div key={noteIndex} className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200" style={{ overflow: 'hidden' }}>
                            <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{note.text}</div>
                            <div className="text-gray-400 mt-1">{formatDateTime(note.timestamp)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Quality & Compliance — Quality Events */}
          {effectiveQEs.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Quality Management System Compliance — Quality Events</h4>
              {effectiveQEs.map((qe: any, index: number) => {
                const qeId = qe.stableId || qe.id;
                const isManuallyAdded = qe.matchedVia === 'Manual';
                return (
                  <div key={qeId || index} className={`p-3 rounded border ${isManuallyAdded ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`} style={{ overflow: 'hidden' }}>
                    <div className="flex items-center justify-between mb-2" style={{ flexWrap: 'wrap' as const }}>
                      <div style={{ minWidth: 0 }}>
                        <span className="font-semibold text-gray-900 text-sm">{qe.id}</span>
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded font-medium ${
                          qe.eventType === 'Deviation' ? 'bg-orange-100 text-orange-700' :
                          qe.eventType === 'OutOfSpecification' ? 'bg-red-100 text-red-700' :
                          qe.eventType === 'Complaint' ? 'bg-yellow-100 text-yellow-700' :
                          qe.eventType === 'MarketComplaint' ? 'bg-pink-100 text-pink-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {qe.eventType}
                        </span>
                        {isManuallyAdded && (
                          <span className="ml-2 px-2 py-0.5 text-xs rounded font-medium bg-blue-100 text-blue-700">
                            Manual
                          </span>
                        )}
                      </div>
                    </div>
                    {qe.titleValue && (
                      <p className="text-sm text-gray-500 mb-1">{qe.titleValue}</p>
                    )}
                    <p className="text-sm text-gray-600 mb-1 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '100%' }}>{qe.description}</p>
                    <div className="text-xs text-gray-500 space-y-1">
                      {qe.affectedBatchNumber && <p>Affected Batch: {qe.affectedBatchNumber}</p>}
                      {qe.affectedMarket && <p>Affected Market: {qe.affectedMarket}</p>}
                      <p>Final Approval: {qe.finalApprovalDate || '-'}</p>
                      <p>Target Completion: {qe.targetCompletionDate || '-'}</p>
                      <p>Matched via: {qe.matchedVia}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Certificates & Batch Confirmation */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Certificates</h4>
            <div className="grid grid-cols-2 gap-4">
              <DisplayField fieldId="coa" label="Certificates of Analysis" originalValue={effectiveData?.approvalDetails?.qcTestingSite?.map((a: any) => a?.value).filter(Boolean).join(', ') || ''} />
              <DisplayField fieldId="cocBatchConfirmation" label="CoC for Batch Certification" originalValue={effectiveData?.cmoSupplierQual?.cmoName?.value || ''} />
            </div>
          </div>

          {/* Change Control Notes (general notes from FormPage) */}
          {changeControlNotes.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Change Control Notes ({changeControlNotes.length})</h4>
              <div className="space-y-2">
                {changeControlNotes.map((note, index) => (
                  <div key={index} className="p-3 bg-indigo-50 rounded border border-indigo-200">
                    <div className="text-sm text-gray-900 whitespace-pre-wrap">{note.text}</div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Clock size={12} />
                      {formatDateTime(note.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* BRR Creation Basis & Review Info */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">BRR Review Information</h4>
              <div className="grid grid-cols-2 gap-4">
                {brrCreationBasis.length > 0 && (
                  <div className="p-3 bg-gray-50 rounded border border-gray-200 min-w-0">
                    <div className="text-xs text-gray-500 mb-1">BRR Creation Basis</div>
                    <div className="text-sm font-medium text-gray-900 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{brrCreationBasis.join(', ')}</div>
                  </div>
                )}
                {nextPeriodicReview && (
                  <div className="p-3 bg-gray-50 rounded border border-gray-200 min-w-0">
                    <div className="text-xs text-gray-500 mb-1">Next Periodic Review</div>
                    <div className="text-sm font-medium text-gray-900 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{formatDate(nextPeriodicReview)}</div>
                  </div>
                )}
              </div>
              {countrySpecificRequirements && (
                <div className="p-3 bg-amber-50 rounded border border-amber-200">
                  <div className="text-xs text-amber-700 font-medium mb-1">Country-Specific Requirements</div>
                  <div className="text-sm text-gray-900 whitespace-pre-wrap">{countrySpecificRequirements}</div>
                </div>
              )}
              <div className={`p-3 rounded border-2 ${cpDocsEditedByQP ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-500">CP Internal Documents</div>
                  <div className="flex items-center gap-2">
                    {cpDocsEditedByQP && (
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">Edited</span>
                    )}
                    {!isEditingCpDocs && (
                      <button
                        onClick={() => { setEditingCpDocs([...cpInternalDocs]); setIsEditingCpDocs(true); }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                {isEditingCpDocs ? (
                  <div className="space-y-2">
                    {CP_INTERNAL_DOC_ITEMS.map((item) => (
                      <label key={item.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingCpDocs.includes(item.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditingCpDocs(prev => [...prev, item.value]);
                            } else {
                              setEditingCpDocs(prev => prev.filter(v => v !== item.value));
                            }
                          }}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-gray-700">{item.label}</span>
                      </label>
                    ))}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={async () => {
                          setCpInternalDocs(editingCpDocs);
                          setCpDocsEditedByQP(true);
                          // Small delay to ensure state updates are processed before save
                          await new Promise(resolve => setTimeout(resolve, 10));
                          const success = await handleSaveProgress();
                          if (success) {
                            setIsEditingCpDocs(false);
                          }
                        }}
                        disabled={saving}
                        className={`px-2 py-1 text-white text-xs rounded flex items-center gap-1 ${
                          saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="animate-spin" size={12} />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                      <button
                        onClick={() => { setEditingCpDocs([]); setIsEditingCpDocs(false); }}
                        disabled={saving}
                        className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {CP_INTERNAL_DOC_ITEMS.map((item) => (
                      <div key={item.value} className="flex items-center gap-2 text-sm">
                        <span className={cpInternalDocs.includes(item.value) ? 'text-green-600' : 'text-gray-400'}>
                          {cpInternalDocs.includes(item.value) ? '✓' : '○'}
                        </span>
                        <span className={cpInternalDocs.includes(item.value) ? 'text-gray-900' : 'text-gray-400'}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          {/* Technical Documentation */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Technical Documentation</h4>
            <div className="space-y-2">
              {STANDARD_DOC_SECTIONS.map(({ key, title }) => {
                const sectionData = boxFolderUrls[key];
                const entries = sectionData?.entries || [];
                return (
                  <div key={key} className="p-3 bg-gray-50 rounded border border-gray-200 min-w-0">
                    <div className="text-sm font-medium text-gray-900 mb-2 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{title}</div>
                    {entries.length > 0 ? (
                      <div className="space-y-2">
                        {entries.map((entry, idx) => (
                          <div key={entry.id} className="p-2 bg-white rounded border border-gray-100 min-w-0">
                            <div className="text-xs text-gray-500 mb-1 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              Box Link {entries.length > 1 ? `${idx + 1}` : ''}:{' '}
                              <a href={entry.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{entry.link}</a>
                            </div>
                            {(entry.version || entry.freeText) && (
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                {entry.version && <span className="text-gray-600 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>Version: {entry.version}</span>}
                                {entry.freeText && <span className="text-gray-600 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>Notes: {entry.freeText}</span>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <DisplayField fieldId={`${key}_version`} label="Version" originalValue={documentSectionData[key]?.version || ''} showCompliance={false} />
                        <DisplayField fieldId={`${key}_freeText`} label="Notes" originalValue={documentSectionData[key]?.freeText || ''} showCompliance={false} />
                      </div>
                    )}
                    <ComplianceDropdown fieldId={key} />
                  </div>
                );
              })}
              {customDocSections.map(section => (
                <div key={section.id} className="p-3 bg-gray-50 rounded border border-gray-200 min-w-0">
                  <div className="text-sm font-medium text-gray-900 mb-2 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{section.name}</div>
                  <div className="grid grid-cols-2 gap-4">
                    <DisplayField fieldId={`${section.id}_version`} label="Version" originalValue={section.version || ''} showCompliance={false} />
                    <DisplayField fieldId={`${section.id}_freeText`} label="Notes" originalValue={section.freeText || ''} showCompliance={false} />
                  </div>
                  <ComplianceDropdown fieldId={section.id} />
                  {section.boxFolderUrl && (
                    <div className="mt-2 text-xs text-gray-500 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      Box: <a href={section.boxFolderUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{section.boxFolderUrl}</a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Serialization & Aggregation */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm border-b border-gray-200 pb-2">Serialization & Aggregation</h4>
            <div className="grid grid-cols-2 gap-4">
              {/* Serialization Requirement - Editable */}
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <label htmlFor="serializationRequirement" className="block text-xs font-medium text-gray-500 mb-1">Serialization Requirement Status</label>
                <select
                  id="serializationRequirement"
                  value={serializationRequirement}
                  onChange={(e) => setSerializationRequirement(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select serialization requirement...</option>
                  <option value="yes-required-serialised">Yes, serialisation requirement in the country, goods are serialised</option>
                  <option value="no-requirement-not-serialised">No, no requirement in the destination country, goods are NOT serialised</option>
                  <option value="no-requirement-serialised-nonetheless">No, no requirement in the destination country, goods are serialised nonetheless</option>
                  <option value="na">N/A</option>
                </select>
              </div>
              <DisplayField fieldId="tracelinkGtin" label="GTIN (Product Code)" originalValue={tracelinkGtin} />
              <DisplayField fieldId="tracelinkQuantity" label="Quantity" originalValue={tracelinkQuantity} />
              {tracelinkHierarchyLevels.length > 0 && (
                <DisplayField
                  fieldId="tracelinkHierarchyLevels"
                  label="Quantity by Packaging Level"
                  originalValue={tracelinkHierarchyLevels.map(l => `${l.level}: ${l.quantity.toLocaleString()}`).join(', ')}
                />
              )}
              {/* Aggregation Requirement - Editable */}
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <label htmlFor="aggregationRequirement" className="block text-xs font-medium text-gray-500 mb-1">Aggregation Requirement Status</label>
                <select
                  id="aggregationRequirement"
                  value={aggregationRequirement}
                  onChange={(e) => setAggregationRequirement(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select aggregation requirement...</option>
                  <option value="yes-required-aggregated">Yes, aggregation requirement in the country, goods are aggregated</option>
                  <option value="no-requirement-not-aggregated">No, no requirement in the destination country, goods are NOT aggregated</option>
                  <option value="no-requirement-aggregated-nonetheless">No, no requirement in the destination country, goods are nevertheless aggregated</option>
                  <option value="na">N/A</option>
                </select>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Additional Evidence Items (Unified: PDFs + Box Links) */}
      {additionalEvidenceItems && additionalEvidenceItems.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText size={18} className="text-gray-600" />
            Additional Evidence ({additionalEvidenceItems.length})
          </h3>
          <div className="space-y-3">
            {additionalEvidenceItems.map((item) => (
              <div
                key={item.id}
                className={`p-4 rounded-lg border ${
                  item.type === 'pdf' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
                } ${editingItemId === item.id ? 'ring-2 ring-blue-400' : ''}`}
              >
                {editingItemId === item.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !editingTitle.trim() ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      {!editingTitle.trim() && (
                        <p className="text-xs text-red-500 mt-1">Title is required</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editingDescription}
                        onChange={(e) => setEditingDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                      />
                    </div>
                    {item.type === 'boxLink' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Box Link <span className="text-red-500">*</span></label>
                        <input
                          type="url"
                          value={editingBoxLink}
                          onChange={(e) => setEditingBoxLink(e.target.value)}
                          placeholder="https://cheplapharm.app.box.com/..."
                          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            editingBoxLink && !isValidBoxUrl(editingBoxLink) ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                        />
                        {editingBoxLink && !isValidBoxUrl(editingBoxLink) && (
                          <p className="text-xs text-red-500 mt-1">URL must start with https://cheplapharm.app.box.com/ or https://app.box.com/</p>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={!canSaveEdit()}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <CheckCircle size={14} /> Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded ${item.type === 'pdf' ? 'bg-green-100' : 'bg-blue-100'}`}>
                      {item.type === 'pdf' ? (
                        <FileText size={18} className="text-green-600" />
                      ) : (
                        <ExternalLink size={18} className="text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{item.title}</div>
                      {item.description && (
                        <p className="text-sm text-gray-600 mt-1 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{item.description}</p>
                      )}
                      {item.type === 'pdf' ? (
                        <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                          <span className="text-xs text-gray-500 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                            {item.fileName} &bull; {item.fileSize}
                          </span>
                          {hasDirectBlobLink(item) && (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              View <ExternalLink size={14} />
                            </a>
                          )}
                        </div>
                      ) : (
                        <a
                          href={item.boxLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 mt-2 inline-flex items-center gap-1 break-all"
                          style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        >
                          {item.boxLink} <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                    {setAdditionalEvidenceItems && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEditItem(item)}
                          className={`p-1.5 rounded transition-colors ${
                            item.type === 'pdf' ? 'text-green-600 hover:bg-green-100' : 'text-blue-600 hover:bg-blue-100'
                          }`}
                          title="Edit item"
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Remove item"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Progress Section */}
      {ENABLE_SAVE_FUNCTIONALITY && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSaveProgress}
                disabled={saving || isNavigationBlocked}
                className={`flex items-center gap-2 px-4 py-2 font-medium rounded transition-colors ${
                  saving || isNavigationBlocked
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {saving || isNavigationBlocked ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    {saving ? 'Saving...' : 'Please wait...'}
                  </>
                ) : (
                  <>
                    <FileText size={16} />
                    Save Progress
                  </>
                )}
              </button>
              {lastSaved && (
                <span className="text-sm text-gray-500">
                  Last saved: {formatDateTime(lastSaved.toISOString())}
                </span>
              )}
            </div>
            {saveError && (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle size={16} />
                <span className="text-sm">{saveError}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Download Buttons */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 no-print">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText size={18} className="text-green-600" />
          {boxFolderId ? 'Export QP Submission Documents' : 'Download QP Submission Documents'}
        </h3>
        {boxUploadResult && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2 text-sm text-green-800">
            <CheckCircle size={16} className="text-green-600 shrink-0" />
            <span>
              <strong>{boxUploadResult.filename}</strong> uploaded to Box folder.{' '}
              <a
                href={`https://cheplapharm.app.box.com/folder/${boxUploadResult.boxFolderId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Open folder
              </a>
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleDownloadWord}
            disabled={savingBeforeDownload !== null}
            className="py-3 px-6 bg-sky-400 hover:bg-sky-500 text-white font-medium rounded transition-colors flex items-center justify-center gap-2 disabled:bg-sky-300 disabled:cursor-not-allowed"
          >
            {savingBeforeDownload === 'word' ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {boxFolderId ? 'Exporting to Box...' : 'Saving...'}
              </>
            ) : (
              <>
                <FileText size={18} />
                {boxFolderId ? 'Export BRR Form to Box' : 'Download BRR Form'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Delete BRR Button */}
      {onDeleteBRR && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <DeleteBRRButton onDelete={onDeleteBRR} />
        </div>
      )}
    </div>
  );
};

export const ConfirmationPage = React.memo(ConfirmationPageComponent);
